import {
  CheckCircle2,
  ChevronDown,
  Flag,
  Loader2,
  MoreHorizontal,
  Pencil,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Subheading } from '@/components/ui/catalyst/heading';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useMatter } from '@/hooks/useMatter';
import {
  useCreateTreatmentBarrierMutation,
  useDeleteTreatmentBarrierMutation,
  useGetTreatmentBarriersQuery,
  useUpdateTreatmentBarrierMutation,
} from '@/services/api/treatmentService';
import {
  TreatmentBarrier,
  TreatmentBarrierStatus,
  TreatmentBarrierType,
} from '@/services/types/treatment-types';

import { BarrierType, barrierTypes } from '../constants';

interface FlagIssueFormProps {
  checkInId: string;
}

export const FlagIssueForm = ({ checkInId }: FlagIssueFormProps) => {
  const { matterId } = useMatter();
  const { data: treatmentBarriers, isLoading } = useGetTreatmentBarriersQuery({
    matterId,
  });
  const [createTreatmentBarrier] = useCreateTreatmentBarrierMutation();
  const [deleteTreatmentBarrier] = useDeleteTreatmentBarrierMutation();
  const [updateTreatmentBarrier] = useUpdateTreatmentBarrierMutation();

  const [showResolved, setShowResolved] = useState(false);

  const [editingIssue, setEditingIssue] = useState<TreatmentBarrier | null>(
    null,
  );
  const [editingNotes, setEditingNotes] = useState('');

  const [currentIssue, setCurrentIssue] = useState<{
    barrierType: TreatmentBarrierType | '';
    notes: string;
  }>({ barrierType: '', notes: '' });

  const handleAddIssue = async () => {
    if (!currentIssue.barrierType || !currentIssue.notes) return;

    const { error } = await createTreatmentBarrier({
      matterId,
      payload: {
        barrier_type: currentIssue.barrierType,
        notes: currentIssue.notes,
        discovered_in_check_in_id: checkInId,
      },
    });

    if (error) {
      toast.error('Failed to create treatment barrier');
      return;
    }

    setCurrentIssue({ barrierType: '', notes: '' });
  };

  const handleRemoveIssue = async (barrierId: string) => {
    const { error } = await deleteTreatmentBarrier({
      matterId,
      barrierId,
    });
    if (error) {
      toast.error('Failed to delete treatment barrier');
    }
  };

  const handleUpdateIssue = (issue: TreatmentBarrier) => {
    setEditingIssue(issue);
    setEditingNotes(issue.notes);
  };

  const handleCancelUpdate = () => {
    setEditingIssue(null);
    setEditingNotes('');
  };

  const handleSaveUpdate = async () => {
    if (!editingIssue) return;
    const { error } = await updateTreatmentBarrier({
      matterId,
      barrierId: editingIssue.id,
      payload: { id: editingIssue.id, notes: editingNotes },
    });
    if (error) {
      toast.error('Failed to update treatment barrier');
    } else {
      toast.success('Issue updated successfully');
      handleCancelUpdate();
    }
  };

  const handleResolveIssue = async (issue: TreatmentBarrier) => {
    const { error } = await updateTreatmentBarrier({
      matterId,
      barrierId: issue.id,
      payload: { id: issue.id, status: TreatmentBarrierStatus.RESOLVED },
    });

    if (error) {
      toast.error('Failed to resolve treatment barrier');
    } else {
      toast.success('Issue resolved successfully');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleAddIssue();
    }
  };

  const renderResolvedIssue = (issue: TreatmentBarrier) => (
    <li
      key={issue.id}
      className="p-3 pr-2 bg-green-50 border border-green-200 rounded-md text-sm flex justify-between items-center"
    >
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-5 w-5 text-green-600" />
        <div>
          <strong className="font-semibold text-green-800 line-through">
            {
              barrierTypes.find(
                (bt: BarrierType) => bt.value === issue.barrier_type,
              )?.label
            }
            :{' '}
          </strong>
          <span className="text-green-700 line-through">{issue.notes}</span>
        </div>
      </div>
    </li>
  );

  const renderIssue = (issue: TreatmentBarrier, isNew: boolean) => {
    const isEditing = editingIssue?.id === issue.id;
    return (
      <li
        key={issue.id}
        className={`p-3 pr-2 rounded-md text-sm flex justify-between items-center ${
          isNew
            ? 'bg-red-50 border border-red-200'
            : 'bg-gray-50 border border-gray-200'
        }`}
      >
        {isEditing ? (
          <div className="flex-grow">
            <Textarea
              value={editingNotes}
              onChange={(e) => setEditingNotes(e.target.value)}
              className="mb-2"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancelUpdate}
                type="button"
              >
                Cancel
              </Button>
              <Button size="sm" onClick={handleSaveUpdate} type="button">
                Save
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div>
              <strong
                className={`font-semibold ${
                  isNew ? 'text-red-800' : 'text-gray-800'
                }`}
              >
                {
                  barrierTypes.find(
                    (bt: BarrierType) => bt.value === issue.barrier_type,
                  )?.label
                }
                :{' '}
              </strong>
              <span className={isNew ? 'text-red-700' : 'text-gray-700'}>
                {issue.notes}
              </span>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  type="button"
                  className={`!p-1 rounded-full h-7 w-7 ${
                    isNew
                      ? 'text-red-400 hover:text-red-600 hover:bg-red-100'
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <MoreHorizontal className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleUpdateIssue(issue)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  <span>Update</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleResolveIssue(issue)}>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  <span>Resolve</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleRemoveIssue(issue.id)}
                  className="text-red-600"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  <span>Delete</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </li>
    );
  };

  if (isLoading) {
    return (
      <div className="p-4 bg-white rounded-lg border shadow-sm">
        <Subheading className="flex items-center gap-2 text-gray-800 mb-3">
          <Flag className="h-5 w-5 text-red-500" />
          Loading treatment barriers...
          <Loader2 className="h-5 w-5 text-gray-500 animate-spin" />
        </Subheading>
      </div>
    );
  }

  if (!treatmentBarriers) {
    return (
      <div className="p-4 bg-white rounded-lg border shadow-sm">
        <Subheading className="flex items-center gap-2 text-gray-800 mb-3">
          <Flag className="h-5 w-5 text-red-500" />
          Failed to load treatment barriers
        </Subheading>
      </div>
    );
  }

  const activeBarriers =
    treatmentBarriers?.filter(
      (b) => b.status !== TreatmentBarrierStatus.RESOLVED,
    ) ?? [];

  const resolvedBarriers =
    treatmentBarriers?.filter(
      (b) => b.status === TreatmentBarrierStatus.RESOLVED,
    ) ?? [];

  const newIssues =
    activeBarriers?.filter((b) => b.discovered_in_check_in_id === checkInId) ??
    [];
  const preexistingIssues =
    activeBarriers?.filter((b) => b.discovered_in_check_in_id !== checkInId) ??
    [];

  return (
    <div className="p-4 bg-white rounded-lg border shadow-sm">
      <Subheading className="flex items-center gap-2 text-gray-800 mb-3">
        <Flag className="h-5 w-5 text-red-500" />
        Flag an Issue
      </Subheading>
      <div className="space-y-2">
        <Select
          value={currentIssue.barrierType}
          onValueChange={(v) =>
            setCurrentIssue({
              ...currentIssue,
              barrierType: v as TreatmentBarrierType,
            })
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Type of Issue" />
          </SelectTrigger>
          <SelectContent>
            {barrierTypes.map((type: BarrierType) => (
              <SelectItem key={type.value} value={type.value}>
                <div className="flex items-center gap-2">
                  <type.icon className={`h-4 w-4 ${type.color}`} />
                  <span>{type.label}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex flex-col shadow-md rounded-md border border-input focus-within:ring-1 focus-within:ring-ring p-2 gap-y-2">
          <Textarea
            className="shadow-none border-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-none"
            value={currentIssue.notes}
            onChange={(e) =>
              setCurrentIssue({ ...currentIssue, notes: e.target.value })
            }
            placeholder="Describe the issue..."
            onKeyDown={handleKeyDown}
          />
          <div className="flex justify-end">
            <Button
              onClick={handleAddIssue}
              className="flex items-center gap-x-1.5"
              type="button"
              size="sm"
              disabled={!currentIssue.barrierType || !currentIssue.notes}
            >
              Add ⌘ + ↵
            </Button>
          </div>
        </div>
      </div>

      {newIssues.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <Subheading className="text-gray-600 mb-2 font-medium">
            Flagged in this check-in
          </Subheading>
          <ul className="space-y-2">
            {newIssues.map((issue) => renderIssue(issue, true))}
          </ul>
        </div>
      )}

      {preexistingIssues.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <Subheading className="text-gray-500 mb-2 font-medium">
            Previously Flagged Issues
          </Subheading>
          <ul className="space-y-2">
            {preexistingIssues.map((issue) => renderIssue(issue, false))}
          </ul>
        </div>
      )}

      {resolvedBarriers.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <div className="flex justify-between items-center">
            <Subheading className="text-gray-500 font-medium">
              Resolved Issues ({resolvedBarriers.length})
            </Subheading>
            <Button
              variant="ghost"
              type="button"
              size="sm"
              onClick={() => setShowResolved(!showResolved)}
              className="flex items-center gap-1"
            >
              <span>{showResolved ? 'Hide' : 'Show'}</span>
              <ChevronDown
                className={`h-4 w-4 transition-transform ${
                  showResolved ? 'rotate-180' : ''
                }`}
              />
            </Button>
          </div>
          {showResolved && (
            <ul className="mt-2 space-y-2">
              {resolvedBarriers.map((issue) => renderResolvedIssue(issue))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
